// TechnicalSEOPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/seoxs.PNG'

const TechnicalSEOPage = () => {
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const techStats = [
        { number: '53%', label: 'of mobile users abandon sites that take over 3 seconds to load' },
        { number: '70%', label: 'of crawl budget wasted on low-value pages without proper optimization' },
        { number: '85%', label: 'higher chance of ranking with proper structured data implementation' },
        { number: '40%', label: 'increase in organic traffic from comprehensive technical fixes' }
    ];

    const technicalAreas = [
        {
            icon: 'fas fa-search',
            title: 'Comprehensive Technical SEO Audit',
            desc: 'Detailed assessment of your site infrastructure to uncover technical barriers and prioritize high-impact improvements for maximum visibility.'
        },
        {
            icon: 'fas fa-tachometer-alt',
            title: 'Site Speed & Performance Optimization',
            desc: 'Fine-tuning server response, caching, image optimization, and code efficiency to improve Core Web Vitals and user experience.'
        },
        {
            icon: 'fas fa-robot',
            title: 'Crawlability & Indexing Improvements',
            desc: 'Optimizing robots.txt, XML sitemaps, canonical tags, and handling crawl errors to ensure search engines can efficiently discover and index your content.'
        },
        {
            icon: 'fas fa-mobile-alt',
            title: 'Mobile-First Optimization',
            desc: 'Ensuring responsive design, mobile performance, and proper implementation for mobile-first indexing to meet modern search requirements.'
        },
        {
            icon: 'fas fa-code',
            title: 'Structured Data & Schema Markup',
            desc: 'Implementation of JSON-LD schema markup to help search engines understand content context and enhance opportunities for rich results.'
        },
        {
            icon: 'fas fa-sitemap',
            title: 'URL Architecture & Internal Linking',
            desc: 'Developing clear, logical site structures with optimized URL hierarchies and strategic internal linking for better crawling and user navigation.'
        },
        {
            icon: 'fas fa-shield-alt',
            title: 'Security & HTTPS Implementation',
            desc: 'Ensuring proper SSL/TLS implementation, security headers, and protection against vulnerabilities that impact both user trust and search ranking.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Ongoing Monitoring & Maintenance',
            desc: 'Continuous performance tracking, issue monitoring, and technical strategy adaptation to keep pace with evolving search algorithms.'
        }
    ];

    const benefits = [
        ['Improved Search Visibility', 'More pages are found, indexed, and ranked effectively by search engines'],
        ['Faster User Experience', 'Enhanced Core Web Vitals lead to longer user sessions and higher engagement rates'],
        ['Better Indexing Efficiency', 'Key content gets indexed promptly with optimized crawl budget allocation'],
        ['Enhanced Mobile Performance', 'Superior mobile experience improves rankings and user satisfaction'],
        ['Stronger SEO Foundation', 'Robust technical framework supports and amplifies all other SEO strategies']
    ];

    const targetBusinesses = [
        'E-commerce platforms with complex product structures',
        'Corporate and enterprise websites requiring scalable solutions',
        'Content-heavy portals and blogs with extensive archives',
        'Sites undergoing redesign, migration, or platform changes',
        'Brands experiencing technical debt or performance issues',
        'Businesses aiming for long-term, sustainable organic growth'
    ];

    const techQuestions = [
        'Can search engines efficiently crawl and index your important pages?',
        'Is your site speed affecting user experience and search rankings?',
        'Do you have proper structured data implementation for rich results?',
        'Is your site optimized for mobile-first indexing?',
        'What technical barriers are preventing your content from ranking?'
    ];

    return (
        <div className="seo sv-page">
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Technical SEO</span> Services
                    </h1>
                    <p>Strengthening your website's foundation for sustainable organic growth</p>
                </div>
            </div>

            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    <span className="sv-keyword-highlight">Technical SEO</span> is the backbone of organic visibility — it ensures that search engines can find, crawl, index, and understand your website effectively. Even the best content can struggle to rank if the technical framework of your site isn't sound.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>Technical SEO services</strong> focus on building a robust foundation that supports all other SEO efforts and drives sustainable organic growth. We ensure search engines can access and interpret your pages, your site loads fast and performs reliably, and users enjoy a frictionless experience.
                                </p>
                                <p className="sv-description">
                                    Technical SEO ensures search engines can access and interpret your pages, your site loads fast and performs reliably, and users enjoy a frictionless experience across all devices.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Technical SEO Audit
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Technical SEO Matters</span> for Your Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {techStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Technical SEO?</h2>
                                <p className="sv-section-description">
                                    <strong>Technical SEO</strong> refers to the process of optimizing your website's infrastructure so that search engines can effectively crawl, index, and understand your content. It addresses the non-content elements of your site to improve its organic search performance.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our Technical SEO Services Answer:</h4>
                                    {techQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in Technical SEO?</h2>
                                <p className="sv-section-description">
                                    Technical SEO lays the groundwork for every successful SEO strategy. Without it, even expert keyword and content work can fall short. A robust technical framework supports and amplifies all other SEO efforts.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-search"></i>
                                        <h5>Crawlability</h5>
                                        <p>Ensure search engines can efficiently discover and index your content</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-tachometer-alt"></i>
                                        <h5>Site Speed</h5>
                                        <p>Improve Core Web Vitals for better user experience and rankings</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-mobile-alt"></i>
                                        <h5>Mobile Performance</h5>
                                        <p>Superior mobile experience improves rankings and user satisfaction</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Security</h5>
                                        <p>Proper HTTPS implementation builds trust and satisfies search requirements</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Technical SEO Services at Reachtix</h2>
                                <p className="sv-section-description">
                                    We provide comprehensive technical optimization that addresses every aspect of your website's infrastructure for maximum search performance.
                                </p>

                                <div className="sv-approaches-grid">
                                    {technicalAreas.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Technical SEO With Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Technical Improvement</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([improvement, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{improvement}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in Technical SEO?</h2>
                                <p className="sv-section-description">
                                    Technical SEO is essential for websites that aim for sustainable organic growth and superior search performance:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    No matter the size of your site, building a strong technical foundation is key to achieving sustainable organic success.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Unlock Maximum Organic Potential?</h2>
                                <p className="sv-cta-description">
                                    Technical SEO lays the groundwork for every successful SEO strategy. Without it, even expert keyword and content work can fall short.
                                </p>
                                <p className="sv-cta-description">
                                    Let Reachtix build the technical foundation that will support your long-term search success and organic growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Technical Audit
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Contact Us Today
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TechnicalSEOPage;